import React, { useEffect, useState } from "react";
import {
  AddSquareIcon,
  CustomerIcon,
  ExpensesIcon,
} from "../../../assets/icons";
import { useNavigate } from "react-router-dom";
import Loading from "../InfoCard/InfoCard/Loading";
import { formatValue, useGetPaymentCurrencyList } from "../../../utils";
import { BlueInvoiceIcon } from "../../../assets/icons/BlueInvoiceIcon";
import { BlueInventoryIcon } from "../../../assets/icons/BlueInventoryIcon";
import { useCompanyStatsData } from "../../../hooks/Stats/comapnyStats";
import BusinessTypeChecker from "../../../utils/businessTypeChecker";
import { CreateCustomerModal } from "../../Sales/customer/createCustomer";
import { ICreateSalesData, ICustomer } from "../../../@types";
import { useCompanyStore } from "../../../state/useCompanyStore";
import { useCustomerStore } from "../../../state";
import { CreateCustomer } from "../../customers";

interface StatCardProps {
  title: string;
  icon: any;
  isLoading: boolean;
  buttonTitle: string;
  dataLoaded: boolean;
  value: string;
  onButtonClick: () => void;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  buttonTitle,
  icon,
  dataLoaded,
  value,
  isLoading,
  onButtonClick,
}) => {
  return (
    <div className="flex flex-col relative w-full h-[185px] items-center justify-center p-4 border border-gray-200 bg-white rounded-sm ">
      {isLoading && !dataLoaded ? (
        <Loading />
      ) : (
        <>
          <div className="text-[16px] md:text-[20px] lg:text-[20px] font-semibold">
            {value}
          </div>
          <div className="flex items-center mt-2">
            <span className="px-1">{icon}</span>
            <span className="#191919 text-[14px] md:text-[16px] lg:text-[16px]">
              {title}
            </span>
          </div>
          <div className="flex flex-col cursor-pointer mt-[2em]">
            <button
              className="border border-gray-200 text-[12px] text-[#044E97] font-normal flex px-[1em] py-1 rounded-md "
              onClick={onButtonClick}
            >
              {buttonTitle}
              <span className="pl-2">
                {" "}
                <AddSquareIcon />
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const StatsContainer: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSideBarOpen, setIsSidebarOpen] = useState(false); 
    const companyProfile = useCompanyStore();
    const { userCurrencyCode } = useGetPaymentCurrencyList(
      companyProfile.company,
    );
     const fetchData = useCustomerStore((state) => state.fetchData);
       const createCustomer = useCustomerStore((state) => state.createCustomer);
    const [customer, setCustomer] = React.useState<ICustomer | null>(null);
   const [formData, setFormData] = React.useState<ICreateSalesData>({
      additionalInformation: "",
      customerId: "",
      description: "",
      currency: userCurrencyCode.currency,
      discount: 0,
      inventories: [
        {
          id: "",
          name: "",
          amount: 0,
          quantity: 0,
          rate: 0,
          variations: [
            {
              type: "",
              name: "",
              id: "",
              price: 0,
              quantity: 0,
            },
          ],
        },
      ],
      services: [{ id: "", name: "", duration: "", price: 0 }],
      invoiceDueAt: "",
      invoiceNumber: "",
      issueDate: new Date().toISOString().substring(0, 10),
      isReoccurring: false,
      isReoccurringEndDate: "",
      isReoccurringStartDate: "",
      reoccurringGroupName: "",
      status: "pending",
      tax: 0,
      isPaymentCompleted: false,
      shouldSendEmail: true,
      exchangeRate: 1,
      paymentMethod: "",
      invoiceType: "inventory",
      logoBase64: "",
    });
  const today = new Date();
  const startDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    0,
    0,
    0
  );
  const endDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    23,
    59,
    59
  );
  const { companyData, error } = useCompanyStatsData(startDate, endDate);

  if (error) {
    return <div>{error}</div>;
  }

   const handleSetCustomer = (selectedCustomer: ICustomer | null) => {
      if (selectedCustomer) {
        setCustomer(selectedCustomer);
        if (selectedCustomer.id) {
          setFormData((prevFormData: any) => ({
            ...prevFormData,
            customerId: selectedCustomer.id,
            name: `${selectedCustomer.firstName} ${selectedCustomer.lastName}`,
          }));
        }
      } else {
        setCustomer(null);
        setFormData((prevFormData: any) => ({
          ...prevFormData,
          customerId: "",
          name: "",
        }));
      }
    };
  

  return (
    <div className="w-full py-5">
      <div className="lg:text-[16px] font-medium text-[#000000] mb-4  flex justify-end">
        Today
      </div>
      <div className="lg:flex lg:divide-x :divide-gray-200 grid grid-cols-2 grid-rows-2">
        <StatCard
          value={formatValue(companyData?.totalInvoices || 0)}
          title="Invoices"
          buttonTitle="Create Invoice"
          icon={<BlueInvoiceIcon />}
          dataLoaded={false}
          isLoading={isLoading}
          onButtonClick={() => navigate("/invoices/create")}
        />
        <StatCard
          value={formatValue(companyData?.totalExpenses || 0)}
          title="Expenses"
          buttonTitle="Create Expense"
          icon={<ExpensesIcon color="#044E97" />}
          dataLoaded={false}
          isLoading={isLoading}
          onButtonClick={() => navigate("/expenses/create")}
        />
        <StatCard
          value={formatValue(companyData?.totalCustomers || 0)}
          title="Customers"
          buttonTitle="Add Customer"
          icon={<CustomerIcon color="#044E97" />}
          dataLoaded={false}
          isLoading={isLoading}
          onButtonClick={() => setIsSidebarOpen(true)}
        />
        <BusinessTypeChecker
          renderRetail={() => (
            <StatCard
              value={formatValue(companyData?.totalInventory || 0)}
              title="Inventory"
              buttonTitle="Add Inventory"
              icon={<BlueInventoryIcon />}
              dataLoaded={false}
              isLoading={isLoading}
              onButtonClick={() => navigate("/inventories/create")}
            />
          )}
          renderNonRetail={() => (
            <StatCard
              value={formatValue(companyData?.totalServices || 0)}
              title="Service"
              buttonTitle="Create Service"
              icon={<BlueInventoryIcon />}
              dataLoaded={false}
              isLoading={isLoading}
              onButtonClick={() => navigate("/services/create")}
            />
          )}
        />
      </div>

      <div className="lg:hidden md:hidden">
        {isSideBarOpen && (
          <CreateCustomer
            createCustomers={createCustomer}
            onClose={() => setIsSidebarOpen(false)}
            show={isSideBarOpen}
            customerListNavigate={true}
          />
        )}
      </div>

      {/* Add the modal here */}
      <div className="hidden lg:block md:block">
        {isSideBarOpen && (
          <CreateCustomerModal
            showSideModal={isSideBarOpen}
            setShowSideModal={setIsSidebarOpen}
            handleSetCustomer={handleSetCustomer}
            onCustomerCreate={async (customer: ICustomer | null) => {
              handleSetCustomer(customer);
              await fetchData();
              setIsSidebarOpen(false);
            }}
            onClose={() => setIsSidebarOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default StatsContainer;
